import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

/**
 * Build the compact page-number window (max 5 numbered buttons) with
 * leading/trailing ellipses. Prev/Next stay separate.
 *
 *   totalPages ≤ 5           → [1..N]
 *   current near start (≤3)  → [1, 2, 3, 4, …, N]
 *   current near end (≥N−2)  → [1, …, N−3, N−2, N−1, N]
 *   current in the middle    → [1, …, c−1, c, c+1, …, N]
 */
function buildPageRange(current, totalPages) {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (current <= 3) return [1, 2, 3, 4, "…", totalPages];
  if (current >= totalPages - 2)
    return [1, "…", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  return [1, "…", current - 1, current, current + 1, "…", totalPages];
}

/**
 * Shared pagination widget.
 * - Numbered buttons capped at 5 with ellipses (sliding window)
 * - Range indicator "start–end von total"
 * - Page-size selector (10 / 20 / 50); persists in sessionStorage per key
 *   so refresh within the tab keeps the choice, new tabs/sessions revert
 *   to 10.
 */
export function Pagination({
  page,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
  rangeStart,
  rangeEnd,
  total,
  testId = "pagination",
}) {
  const { t } = useTranslation();
  const items = buildPageRange(page, totalPages);
  return (
    <div
      className="flex flex-col items-center justify-between gap-3 rounded-lg border border-border-subtle bg-surface px-4 py-3 sm:flex-row"
      data-testid={testId}
    >
      <div className="flex items-center gap-3 text-xs text-text-secondary">
        <span data-testid={`${testId}-range`}>
          {t("pagination.range", { start: rangeStart, end: rangeEnd, total })}
        </span>
        <div className="flex items-center gap-2">
          <span className="hidden sm:inline">{t("pagination.per_page")}</span>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => onPageSizeChange(Number(v))}
          >
            <SelectTrigger
              className="h-8 w-[76px] bg-surface border-border-subtle text-xs"
              data-testid={`${testId}-page-size`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((n) => (
                <SelectItem
                  key={n}
                  value={String(n)}
                  data-testid={`${testId}-page-size-${n}`}
                >
                  {n}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          data-testid={`${testId}-prev`}
          aria-label={t("pagination.prev")}
          className="h-8 px-2 text-text-primary hover:bg-offwhite disabled:opacity-40"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        {items.map((p, i) =>
          p === "…" ? (
            <span
              key={`ellipsis-${i}`}
              className="px-1 text-xs text-text-secondary"
              aria-hidden
            >
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              data-testid={`${testId}-page-${p}`}
              aria-current={p === page ? "page" : undefined}
              className={`inline-flex h-8 min-w-[32px] items-center justify-center rounded-md px-2 text-sm transition-colors ${
                p === page
                  ? "bg-champagne text-charcoal font-semibold"
                  : "text-text-primary hover:bg-offwhite"
              }`}
            >
              {p}
            </button>
          )
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          data-testid={`${testId}-next`}
          aria-label={t("pagination.next")}
          className="h-8 px-2 text-text-primary hover:bg-offwhite disabled:opacity-40"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Small hook to bind a page-size selector to sessionStorage. Session-only:
 * new tab/session reverts to the default (10 per the spec).
 */
export function useSessionPageSize(storageKey, defaultSize = 10) {
  const [pageSize, setPageSizeState] = React.useState(() => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      const n = raw ? Number(raw) : NaN;
      return PAGE_SIZE_OPTIONS.includes(n) ? n : defaultSize;
    } catch {
      return defaultSize;
    }
  });
  const setPageSize = React.useCallback(
    (n) => {
      const size = PAGE_SIZE_OPTIONS.includes(n) ? n : defaultSize;
      setPageSizeState(size);
      try { sessionStorage.setItem(storageKey, String(size)); } catch { /* ignore */ }
    },
    [storageKey, defaultSize]
  );
  return [pageSize, setPageSize];
}

export { PAGE_SIZE_OPTIONS, buildPageRange };
